'use client';

import classNames from 'classnames';
import {
    CLPPurchaseOption,
    useClpPurchaseContext,
    useClpSelectedOption,
} from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import {publishEvent} from '@udemy-v2/event-tracking';
import styles from './subscription-purchase-content.module.scss';
import {useCommonData} from '@udemy-v2/common-data';
import {useFormatNumber, useI18n} from '@udemy-v2/i18n';
import {useClpNoticeContext} from '@/features/course-landing-page/context/clp-notice-context-provider.react-component';
import {
    aggregateCollectionStats,
    FALLBACK_COLLECTION_STATS,
    PLAN_SELECTION_CHECKOUT_URL,
    PlanPeriodStyle,
    SubscribeFromPageCookie,
    SubscriptionCTA,
    SubscriptionCTAClickEvent,
    SubscriptionLearnMoreEvent,
    SubscriptionOfferPresented,
    usePersonalPlanCollectionStats,
} from '@udemy-v2/react-subscription-browse';
import {SubscriptionPlanProductType} from '@udemy-v2/subscription-graphql';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {useClpSubscriptionContext} from '@/features/course-landing-page/context/clp-subscription-context.react-component';
import {useDeviceType} from '@udemy-v2/hooks';

const UI_REGION = 'purchase_section_tab0';
const PERSONAL_PLAN_COURSE_COUNT = 27000;
const SUBSCRIPTION_ATTRIBUTION_PATHS = ['/personal-plan', '/pricing'];

export const SubscriptionPurchaseContent = () => {
    const {gettext, interpolate} = useI18n();
    const {formatNumber} = useFormatNumber();
    const deviceType = useDeviceType();
    const isMobile = deviceType === 'mobile';
    const commonData = useCommonData();

    const {
        isLoading,
        unwrappedConsumerSubscriptionPlan,
        subscriptionCTAConfiguration,
        subscriptionTiersEnabled,
    } = useClpPurchaseContext();
    const {selectedPurchaseOption} = useClpSelectedOption();
    const {isCourseInValuePlanCatalog, unwrappedPersonalPlan, subscribedProductType} =
        useClpSubscriptionContext();
    const isGwgStandaloneSubscriber =
        subscribedProductType === SubscriptionPlanProductType.GrowWithGoogle;

    const clpNoticeContext = useClpNoticeContext();
    const course = useClpCourse();
    const subscriptionPriceBoxNotice = clpNoticeContext?.subscriptionPriceBoxNoticeData;

    const defaultTitle = gettext('Subscribe to Udemy\u2019s top courses');
    const title = subscriptionPriceBoxNotice?.title ?? defaultTitle;

    const contentCollections = unwrappedConsumerSubscriptionPlan?.contentCollections;
    const subscriptionPlanStats = contentCollections
        ? aggregateCollectionStats(
              contentCollections.map((contentCollection) => contentCollection.stats),
          )
        : FALLBACK_COLLECTION_STATS;

    const personalPlanStats = usePersonalPlanCollectionStats();
    const personalPlanCourseCount =
        personalPlanStats && 'numberOfCourses' in personalPlanStats
            ? personalPlanStats.numberOfCourses
            : isGwgStandaloneSubscriber && contentCollections
              ? subscriptionPlanStats.numberOfCourses
              : PERSONAL_PLAN_COURSE_COUNT;

    const tiersCourseCount = personalPlanCourseCount;
    const defaultSubtitle =
        subscriptionTiersEnabled && isCourseInValuePlanCatalog
            ? interpolate(
                  gettext(
                      'Get this premium course, plus %(courses_count)s+ of our top-rated courses with Personal Plan.',
                  ),
                  {courses_count: formatNumber(tiersCourseCount)},
                  true,
              )
            : subscriptionTiersEnabled
              ? interpolate(
                    gettext(
                        'Get this premium course, plus %(courses_count)s+ of our top-rated courses with Personal Plan.',
                    ),
                    {courses_count: formatNumber(tiersCourseCount)},
                    true,
                )
              : interpolate(
                    gettext(
                        'Get this course, plus %(courses_count)s+ of our top-rated courses, with Personal Plan.',
                    ),
                    {courses_count: formatNumber(subscriptionPlanStats.numberOfCourses)},
                    true,
                );
    const description = subscriptionPriceBoxNotice?.subtitle ?? defaultSubtitle;

    const learnMoreText = subscriptionPriceBoxNotice?.subtitle_link_text ?? gettext('Learn more');
    const subscriptionContextLearnMoreUrl =
        unwrappedConsumerSubscriptionPlan?.urlLearnMore ?? 'https://udemy.com/personal-plan/';
    const learnMoreUrl =
        subscriptionPriceBoxNotice?.subtitle_link_url ?? subscriptionContextLearnMoreUrl;
    const learnMoreTarget =
        subscriptionPriceBoxNotice?.subtitle_link_target === 'new_tab' ? '_blank' : '_self';

    const handleLearnMoreClick = () => {
        // When navigating to Personal Plan info / plan selection pages from CLP, set the
        // subscribe_from_page cookie so subscription checkout can attribute to this course.
        if (SUBSCRIPTION_ATTRIBUTION_PATHS.some((path) => learnMoreUrl.includes(path))) {
            SubscribeFromPageCookie.set('course_landing_page', parseInt(course.id, 10));
        }

        if (unwrappedConsumerSubscriptionPlan) {
            publishEvent(
                new SubscriptionLearnMoreEvent({
                    planId: unwrappedConsumerSubscriptionPlan.id,
                    planType: unwrappedConsumerSubscriptionPlan.productType,
                    uiRegion: UI_REGION,
                    planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds,
                }),
            );
        }
    };

    const handleCtaClick = () => {
        SubscribeFromPageCookie.set('course_landing_page', parseInt(course.id, 10));
        if (unwrappedConsumerSubscriptionPlan) {
            publishEvent(
                new SubscriptionCTAClickEvent({
                    planId: unwrappedConsumerSubscriptionPlan.id,
                    planType: unwrappedConsumerSubscriptionPlan.productType,
                    ctaType: unwrappedConsumerSubscriptionPlan.trialPeriodDays ? 'trial' : 'paid',
                    uiRegion: UI_REGION,
                    courseId: parseInt(course.id, 10),
                    planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds,
                }),
            );
        }
    };

    // When subscription tiers are enabled, VP/Starter courses display PP pricing as the headline price
    // even though the CTA directs users to the plan selection page. This is a product decision to
    // surface the PP price as the primary offer on all subscription-eligible CLPs.
    const showPPPrice =
        subscriptionTiersEnabled && isCourseInValuePlanCatalog && unwrappedPersonalPlan;

    const handleOfferImpression = unwrappedConsumerSubscriptionPlan
        ? () => {
              publishEvent(
                  new SubscriptionOfferPresented({
                      subscriptionPlan: unwrappedConsumerSubscriptionPlan,
                      productType: unwrappedConsumerSubscriptionPlan.productType,
                      countryCode: commonData?.user?.country ?? '',
                      courseId: parseInt(course.id, 10),
                      courseTrackingId: course.frontendTrackingId ?? null,
                      uiRegion: UI_REGION,
                  }),
              );
          }
        : undefined;

    if (!unwrappedConsumerSubscriptionPlan) {
        return null;
    }

    return (
        <div data-purpose="subscription-purchase-section">
            <div>
                <div className={classNames(styles.title, 'ud-heading-serif-xxl')}>{title}</div>
                <div className={classNames(styles.description, 'ud-text-sm')}>
                    {description}
                    <>
                        {' '}
                        <a
                            className={styles['learn-more-link']}
                            data-purpose="subscription-learn-more-link"
                            href={learnMoreUrl}
                            target={learnMoreTarget}
                            onClick={handleLearnMoreClick}
                        >
                            {learnMoreText}
                        </a>
                    </>
                </div>
            </div>
            <SubscriptionCTA
                className={styles['subscription-cta']}
                uiRegion={UI_REGION}
                subtitleClassName={styles['cta-subtitle']}
                data-purpose="subscription-purchase-button"
                offerCtaStyle={
                    CLPPurchaseOption.SUBSCRIPTION === selectedPurchaseOption
                        ? 'primary'
                        : 'secondary'
                }
                onCTAClick={handleCtaClick}
                courseId={parseInt(course.id, 10)}
                includeTrialDaysOnCta={false}
                plan={showPPPrice ? unwrappedPersonalPlan : unwrappedConsumerSubscriptionPlan}
                trackImpression={handleOfferImpression}
                noticeData={
                    subscriptionCTAConfiguration?.upgradeUrl
                        ? {
                              ...subscriptionPriceBoxNotice,
                              cta_1_url: subscriptionCTAConfiguration.upgradeUrl,
                          }
                        : showPPPrice
                          ? {
                                ...subscriptionPriceBoxNotice,
                                cta_1_url: `${PLAN_SELECTION_CHECKOUT_URL}?product_type=${unwrappedConsumerSubscriptionPlan.productType}`,
                            }
                          : subscriptionPriceBoxNotice
                }
                noticeDataLoading={isLoading}
                directCheckout={subscriptionCTAConfiguration?.directCheckout}
                selectPlanText={subscriptionCTAConfiguration?.selectPlanText}
                purchaseCtaText={subscriptionCTAConfiguration?.purchaseCtaText}
                planPeriodStyle={
                    isMobile
                        ? PlanPeriodStyle.MULTILINE_NO_MARGIN
                        : PlanPeriodStyle.MULTILINE_MARGIN
                }
            />
        </div>
    );
};
